import * as React from "react";
import { IonPage, useIonViewWillLeave, useIonViewDidLeave } from "@ionic/react";

import {
  Context,
  fetchGearboxes,
  createGearbox,
  updateGearbox,
  deleteGearbox,
  fetchGearboxTemplates,
  fetchMyGearboxTemplates,
  createGearboxTemplate,
  updateGearboxTemplate,
  reorderGearboxTemplates,
  deleteGearboxTemplate,
  clearSuccessMessage,
  CREATE_GEARBOX_SUCCESS_MESSAGE,
  UPDATE_GEARBOX_SUCCESS_MESSAGE,
  DELETE_GEARBOX_SUCCESS_MESSAGE,
  CREATE_GEARBOX_TEMPLATE_SUCCESS_MESSAGE,
  UPDATE_GEARBOX_TEMPLATE_SUCCESS_MESSAGE,
  DELETE_GEARBOX_TEMPLATE_SUCCESS_MESSAGE,
  clearErrorMessage,
  reorderBoxes,
  fetchTeacherGearboxDirectories,
  gearboxHidden,
} from "../../../store/teacher";
import useWaitApiCall from "../../../hooks/useWaitApiCall";
import {
  CreateGearboxParams,
  ITeacherGearbox,
  UpdateGearboxParams,
  CreateGearboxTemplateParams,
  UpdateGearboxTemplateParams,
  GearboxTemplate,
} from "../../../state";
import Toast from "../../../molecules/Toast";
import ManageMenu from "../../../templates/teacher/ManageMenu";

import { Menu, State } from "./types";

const ManageBoxesPage = () => {
  const { dispatch, contextState } = React.useContext(Context);
  const [calling, peep] = useWaitApiCall(contextState);

  const [state, setState] = React.useState<State>({
    editable: false,
    showToast: false,
    message: "",
    toastType: "",
    boxes: [],
    templates: [],
  });

  const [menu, setMenu] = React.useState<Menu>("box");

  const title = menu === "box" ? "BOXを編集" : "オリジナルテンプレートを編集";

  useIonViewDidLeave(() => {
    setMenu("box");
  });

  useIonViewWillLeave(() => {
    setState(s => ({ ...s, editable: false, boxes: [] }));
    dispatch(fetchGearboxes());
  });

  React.useEffect(() => {
    dispatch(fetchGearboxes());
    dispatch(fetchTeacherGearboxDirectories());
    dispatch(fetchMyGearboxTemplates());
    dispatch(fetchGearboxTemplates());
  }, [dispatch]);

  React.useEffect(() => {
    if (
      contextState.success_message === CREATE_GEARBOX_SUCCESS_MESSAGE ||
      contextState.success_message === UPDATE_GEARBOX_SUCCESS_MESSAGE ||
      contextState.success_message === DELETE_GEARBOX_SUCCESS_MESSAGE ||
      contextState.success_message ===
        CREATE_GEARBOX_TEMPLATE_SUCCESS_MESSAGE ||
      contextState.success_message ===
        UPDATE_GEARBOX_TEMPLATE_SUCCESS_MESSAGE ||
      contextState.success_message === DELETE_GEARBOX_TEMPLATE_SUCCESS_MESSAGE
    ) {
      setState(s => ({
        ...s,
        showToast: true,
        message: contextState.success_message,
        toastType: "success",
      }));
      dispatch(clearSuccessMessage());
      dispatch(fetchGearboxes());
      dispatch(fetchTeacherGearboxDirectories());
      dispatch(fetchGearboxTemplates());
      dispatch(fetchMyGearboxTemplates());
    }
  }, [dispatch, contextState.success_message]);

  React.useEffect(() => {
    if (contextState.error) {
      setState(s => ({
        ...s,
        showToast: true,
        message: contextState.error,
        toastType: "danger",
      }));
    }
    return () => dispatch(clearErrorMessage());
  }, [dispatch, contextState.error]);

  const createGearboxFunc = React.useCallback(
    (args: CreateGearboxParams) => {
      dispatch(peep(createGearbox(args)));
    },
    [dispatch, peep],
  );

  const updateGearboxFunc = React.useCallback(
    (args: UpdateGearboxParams) => {
      dispatch(peep(updateGearbox(args)));
    },
    [dispatch, peep],
  );

  const reorderBoxesFunc = React.useCallback(
    (boxes: ITeacherGearbox[]) => {
      dispatch(peep(reorderBoxes(boxes.map(({ id }) => id))));
    },
    [dispatch, peep],
  );

  const deleteGearboxFunc = React.useCallback(
    (id: number) => {
      dispatch(peep(deleteGearbox(id)));
    },
    [dispatch, peep],
  );

  const gearboxHiddenFunc = React.useCallback(
    (id: number) => {
      dispatch(peep(gearboxHidden(id)));
    },
    [dispatch, peep],
  );

  const createTemplateFunc = (args: CreateGearboxTemplateParams) => {
    dispatch(peep(createGearboxTemplate(args)));
  };

  const updateTemplateFunc = (args: UpdateGearboxTemplateParams) => {
    dispatch(peep(updateGearboxTemplate(args)));
  };

  const reorderTemplatesFunc = (templates: GearboxTemplate[]) => {
    dispatch(peep(reorderGearboxTemplates(templates.map(({ id }) => id))));
  };

  const deleteTemplateFunc = (id: number) => {
    dispatch(peep(deleteGearboxTemplate(id)));
  };

  const editFunc = () => {
    setState(s => ({ ...s, editable: !s.editable }));
    if (menu === "box" && state.boxes.length) {
      reorderBoxesFunc(state.boxes);
      return;
    }
    if (menu === "template" && state.templates.length) {
      reorderTemplatesFunc(state.templates);
    }
  };

  return (
    <IonPage>
      <ManageMenu
        title={title}
        calling={calling}
        menu={menu}
        setMenu={setMenu}
        state={state}
        setState={setState}
        createGearbox={createGearboxFunc}
        updateGearbox={updateGearboxFunc}
        deleteGearbox={deleteGearboxFunc}
        gearboxHidden={gearboxHiddenFunc}
        createTemplate={createTemplateFunc}
        updateTemplate={updateTemplateFunc}
        deleteTemplate={deleteTemplateFunc}
        edit={editFunc}
      />
      <Toast
        type={state.toastType}
        showToast={state.showToast}
        onClose={() =>
          setState(s => ({
            ...s,
            showToast: false,
            message: "",
            toastType: "",
          }))
        }
        message={state.message}
      />
    </IonPage>
  );
};

export default ManageBoxesPage;
